import api from '@/api'
import status from '@/utils/status'
import format from '@/utils/format'
import tableHeaders from '@/utils/tableHeaders'

const state = {
	settleSummary: {},
	settleList: {
		count: 1,
		list: [],
		headers: []
	},
	settle: {
		summary: [],
		detail: []
	},
	settleForm: {
		createDate: '',
		type: '',
		client: {
			id: '',
			name: ''
		},
		product: {
			name: '',
			amount: '',
		},
		bank: '',
		amount: '',
		transactDate: '',
		transactName: '',
		memo: '',
		attachment: []
	},
	accountInfo: {
		amount : "",
		paidAmount : "",
		latestTaxDate : "",
		list: []
	},
	settlement: {
		count: 1,
		list: [],
		headers: []
	},
	params: {
		count: 30,
		page: 1
	}
}

const getters = {
	settleSummary: state => state.settleSummary,
	settleList: state => state.settleList,
	settle: state => {
		let result = {...state.settleSummary};
		let settleKeys = Object.keys(status.project_detail.settle);
		let summaryList = []
		settleKeys.forEach(key => {
			summaryList.push({
				key: key,
				name: status.project_detail.settle[key],
				value: format.dataValue(key, result, true),
				color: key == 'unpaidAmount' ? 'red' : 'black'
			})
		})
		let detailList = []
		status.project_detail.settle_list.forEach(item => {
			detailList.push({
				key: item.key, 
				name: item.name,
				value: format.dataValue(item.key, result),
				type: item.type
			})
		})
		return {
			summary: summaryList,
			detail: detailList
		}
	},
	settleForm: state => state.settleForm,
	accountSummary: state => accountSummary,
	accountInfo: state => state.accountInfo,
	settlement: state => state.settlement
}

const mutations = {
	setSettleSummary: (state, data) => {
		state.settleSummary = {...data}
		state.settleSummary.unpaidAmount = data.amount - data.paidAmount
	},
	setSettleList: (state, data) => {
		state.settleList.count = data.count;
		state.settleList.list = [...data.list];
		state.settleList.headers = tableHeaders.accountHistoryTableHeader.map(header => {
			return {
				key: header.headerKey,
				label: header.text,
				// 표 컴포넌트는 field.isSort 로 정렬 화살표를 그린다.
				// 종전에는 존재하지 않는 header.sort 를 sortable 로 넘겨 화살표가 안 나왔다.
				isSort: header.isSort === true,
				formatter: (value, key, item) => {
					if(key.indexOf('Date') >= 0) {
						value = format.date({date: value, onlyDate: true})
					}
					else if(key == 'amount') {
						value = `${format.number(value)}원`
					}
					return value;
				}
			}
		})
	},
	setSettleForm: (state, data) => {
		state.settleForm = {...data};
	},
	setAccountInfo: (state, data) => {
		state.accountInfo = {
			summary: [],
			detail: [],
			info: {}
		}
		let settleEntries = Object.entries(status.project_detail.settle);
		let summaryList = []
		settleEntries.forEach((arr,index) => {
			const settleKey = arr[0];
			summaryList.push({
				key: settleKey,
				name: arr[1],
				value: settleKey === 'unpaidAmount' ? `${format.number(data.amount - data.paidAmount)}원` : format.dataValue(settleKey, data, true),
				color: settleKey === 'unpaidAmount' ? 'red' : 'black'
			})
		})

		//회계 정보 요약
		state.accountInfo.summary = summaryList;
		state.accountInfo.detail = [];
		//고객사별 회계 정보
		data.list.forEach(item => {
			let result = status.project_detail.settle_list.map(el => {
				const copy = { ...el }; // 객체 복사
				copy.value = format.dataValue(copy.key, item);
				if (copy.key === 'amount') {
					copy.value = `${copy.value} / ${format.number(item['paidAmount'])}원`
				} else if(copy.key === 'unpaidAmount') {
					copy.value = `${format.number(item.amount - item.paidAmount)}원`
				} else if(copy.key === 'sample') {
					copy.value = ''
				}
				return copy;
			})
			state.accountInfo.detail.push(result);
		})
		state.accountInfo.info = {...data}
	},
	setSettlement: (state, data) => {
		state.settlement.count = data.count;
		state.settlement.list = [...data.list];
		state.settlement.headers = tableHeaders.accountHistoryTableHeader.map(header => {
			return {
				key: header.headerKey,
				label: header.text,
				// 표 컴포넌트는 field.isSort 로 정렬 화살표를 그린다.
				// 종전에는 존재하지 않는 header.sort 를 sortable 로 넘겨 화살표가 안 나왔다.
				isSort: header.isSort === true,
				formatter: (value, key, item) => {
					if(key.indexOf('Date') >= 0) {
						value = format.date({date: value, onlyDate: true})
					}
					else if(key == 'amount' || key == 'publishedAmount') {
						value = `${format.number(value)}원`
					}
					else if(key === 'category') {
						value = value === 'PUBLISH' ? '발행' : value === 'PAID' ? '입금' : value
					}
					return value;
				}
			}
		})
	},
	resetSettlement: (state, data) => {
		state.settlement.count = 0;
		state.settlement.list = [];
	}
}

const actions = {
	//2025.05.16 회계 정보, 정산정보 api 추가
	//프로젝트 회계 정보 가져오기
	loadAccount: async ({commit}, id) => {
		const res = await api.project.loadAccount(id)
		commit('setAccountInfo', res)
	},
	//회계 정보 등록
	addAccount: async ({commit, dispatch}, payload) => {
		const {id, body} = payload;
		const res = await api.project.addAccount(id, body);
		dispatch('loadAccount', id)
		return res; 
	},
	//회계 정보 수정
	editAccount: async ({commit, dispatch}, payload) => {
		const {id, body} = payload;
		const res = await api.project.editAccount(id, body);
		dispatch('loadAccount', id)
		return res; 
	},
	//회계 정보 삭제
	deleteAccount: async ({commit, dispatch}, payload) => {
		const {id, clientId} = payload;
		const res = await api.project.deleteAccount(id, clientId);
		dispatch('loadAccount', id)
		return res; 
	},
	//정산 목록 가져오기
	loadSettlement: async ({commit}, payload) => {
		const { id, params} = payload;
		const res = await api.project.loadSettlement(id, params)
		commit('setSettlement', res)
	},
	//정산 정보 입력
	addSettlement: async ({commit, dispatch}, payload) => {
		const {id, body, params} = payload;
		const res = await api.project.addSettlement(id, body);
		await Promise.all([
			dispatch('loadSettlement', { id, params }),
			dispatch('loadAccount', id)
		])
		return res; 
	},
	//정산 정보 수정
	editSettlement: async ({commit, dispatch}, payload) => {
		const {id, body, params} = payload;
		const res = await api.project.editSettlement(id, body);
		await Promise.all([
			dispatch('loadSettlement', { id, params }),
			dispatch('loadAccount', id)
		])
		return res; 
	},
	//정산 정보 삭제
	deleteSettlement: async ({commit, dispatch}, payload) => {
		const {id, settleId, params} = payload;
		const res = await api.project.deleteSettlement(id, settleId);
		await Promise.all([
			dispatch('loadSettlement', { id, params }),
			dispatch('loadAccount', id)
		])
		return res; 
	},	
}

export default { namespaced: true, state, getters, mutations, actions }